import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiChevronDown, FiSettings, FiLogOut } from 'react-icons/fi';

import {
  Container,
  UserMenuWrapper,
  UserTrigger,
  Avatar,
  Caret,
  Dropdown,
  DropdownHeader,
  DropdownItem,
  DropdownDivider,
} from './styles';

import { useAuth } from '../../../hooks/auth';

interface IHeaderProps {
  title: string;
}

const getInitials = (firstName?: string, lastName?: string) => {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  const a = f ? f[0] : '';
  const b = l ? l[0] : '';
  return `${a}${b}`.toUpperCase() || '?';
};

const roleLabel = (role?: string) => {
  if (!role) return '';
  if (role === 'admin') return 'Administrador';
  if (role === 'syndicate') return 'Sindicato';
  return role;
};

const Header: React.FC<IHeaderProps> = ({ title }) => {
  const { user, signOut } = useAuth();
  const history = useHistory();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  const goToSettings = useCallback(() => {
    setMenuOpen(false);
    history.push('/settings');
  }, [history]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [menuOpen]);

  const fullName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();
  const initials = getInitials(user?.first_name, user?.last_name);

  return (
    <Container>
      <div>
        <div className="breadcrumb-hint">Painel</div>
        <h2>{title}</h2>
      </div>

      <UserMenuWrapper ref={menuRef}>
        <UserTrigger
          type="button"
          className={menuOpen ? 'open' : ''}
          onClick={() => setMenuOpen(prev => !prev)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <Avatar>{initials}</Avatar>
          <div className="info">
            <span className="name">{fullName || 'Usuário'}</span>
            <span className="role">{roleLabel(user?.role)}</span>
          </div>
          <Caret className={menuOpen ? 'open' : ''}>
            <FiChevronDown size={16} />
          </Caret>
        </UserTrigger>

        {menuOpen && (
          <Dropdown role="menu">
            <DropdownHeader>
              <div className="name">{fullName || 'Usuário'}</div>
              {user?.email && <div className="email">{user.email}</div>}
            </DropdownHeader>
            <DropdownItem type="button" role="menuitem" onClick={goToSettings}>
              <FiSettings size={16} />
              Configurações
            </DropdownItem>
            <DropdownDivider />
            <DropdownItem
              type="button"
              role="menuitem"
              className="danger"
              onClick={handleSignOut}
            >
              <FiLogOut size={16} />
              Sair
            </DropdownItem>
          </Dropdown>
        )}
      </UserMenuWrapper>
    </Container>
  );
};

export default Header;
